import React, { useState, useEffect, useCallback } from "react";
import AdminPostManagement from "./AdminPostManagement";
import AdminAutoPostSettings from "./AdminAutoPostSettings";
import AdminUserDetail from "./AdminUserDetail";

const DEFAULT_AFFILIATE_PERCENT = 30;
const DEFAULT_AFFILIATE_DURATION = 12;
const ADMIN_FOCUS_USER_STORAGE_KEY = "adminFocusUserId";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const getAdminHeaders = () => {
  const token = localStorage.getItem("userAccessToken");
  const adminPassword =
    process.env.REACT_APP_ADMIN_PASSWORD || process.env.ADMIN_PASSWORD;
  return {
    "Content-Type": "application/json",
    ...(adminPassword ? { "X-Admin-Password": adminPassword } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// Import plans từ PricingScreen
const membershipPlans = [
  {
    id: 6,
    name: "Trải nghiệm",
    displayName: "Trải nghiệm (1 ngày)",
    monthlyPoints: 200,
  },
  {
    id: 1,
    name: "Cơ bản",
    displayName: "Cơ bản",
    monthlyPoints: 1000,
  },
  {
    id: 2,
    name: "Chuyên nghiệp",
    displayName: "Chuyên nghiệp",
    monthlyPoints: 2900,
  },
  {
    id: 3,
    name: "Cao Cấp",
    displayName: "Cao Cấp",
    monthlyPoints: 4300,
  },
  {
    id: 4,
    name: "VIP",
    displayName: "VIP",
    monthlyPoints: 7200,
  },
];
const AdminUserManagement = ({ setActiveMenu }) => {
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [viewAutoPostSettings, setViewAutoPostSettings] = useState(false);
  const [detailUserId, setDetailUserId] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showUserModal, setShowUserModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedMemberships, setSelectedMemberships] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    cursor: 0,
    limit: usersPerPage,
    nextCursor: null,
    total: 0,
  });
  const [summary, setSummary] = useState(null);

  const sanitizeUserForSave = (user) => {
    const {
      id,
      postCounts,
      totalPostCount,
      pointsFormatted,
      totalPoints,
      connectedFanpages,
      signupCount,
      ...userData
    } = user;

    return {
      id,
      userData,
    };
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "ascending" ? "↑" : "↓";
    }
    return "";
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Phân trang từ API index
  const currentUsers = filteredUsers;
  const totalPages = Math.ceil((pageInfo.total || 0) / usersPerPage);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        limit: String(usersPerPage),
        cursor: String((currentPage - 1) * usersPerPage),
        search: searchText.trim(),
        fromDate,
        toDate,
        sortBy: sortConfig.key || "createdAt",
        sortOrder: sortConfig.direction === "ascending" ? "asc" : "desc",
      });

      if (selectedMemberships.length > 0) {
        query.set(
          "memberships",
          selectedMemberships
            .map((membership) =>
              membership === null || membership === undefined
                ? "free"
                : String(membership)
            )
            .join(",")
        );
      }

      const response = await fetch(
        `${API_URL}/api/admin/users?${query.toString()}`,
        {
          headers: getAdminHeaders(),
        }
      );
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Không thể tải danh sách người dùng");
      }

      setUsers(result.data || []);
      setFilteredUsers(result.data || []);
      setPageInfo(result.pageInfo || {});
      setSummary(result.summary || null);
    } catch (error) {
      console.error("Lỗi khi tải dữ liệu người dùng:", error);
      setUsers([]);
      setFilteredUsers([]);
      setPageInfo({ cursor: 0, limit: usersPerPage, nextCursor: null, total: 0 });
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    fromDate,
    searchText,
    selectedMemberships,
    sortConfig,
    toDate,
    usersPerPage,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, fromDate, toDate, selectedMemberships, sortConfig]);

  useEffect(() => {
    const focusUserId = localStorage.getItem(ADMIN_FOCUS_USER_STORAGE_KEY);
    if (!focusUserId) {
      return;
    }

    const targetUser = users.find((user) => user.id === focusUserId);
    if (targetUser) {
      handleUserClick(targetUser);
    } else {
      handleUserClick({ id: focusUserId });
    }

    localStorage.removeItem(ADMIN_FOCUS_USER_STORAGE_KEY);
  }, [users]);

  // Xử lý hiển thị chi tiết người dùng - mở tab mới
  const handleUserClick = (user) => {
    window.open(`/admin/users/${user.id}`, '_blank');
  };

  // Xử lý chỉnh sửa người dùng
  const handleEditUser = () => {
    setEditMode(true);
  };

  // Xử lý lưu thông tin người dùng
  const handleSaveUser = async () => {
    try {
      const { id, userData } = sanitizeUserForSave(currentUser);

      // Xử lý membership change
      if (userData.membership) {
        // Nếu đang set membership mới và chưa có ngày hết hạn, set ngày hết hạn 1 tháng từ bây giờ
        if (!userData.membershipExpiry) {
          const oneMonthLater = new Date();
          oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
          userData.membershipExpiry = oneMonthLater.getTime();
        }
      } else {
        // Nếu set membership về null/empty, xóa luôn ngày hết hạn
        userData.membership = null;
        userData.membershipExpiry = null;
      }

      if (
        userData.affiliatePercent === null ||
        userData.affiliatePercent === undefined ||
        userData.affiliatePercent === ""
      ) {
        userData.affiliatePercent = DEFAULT_AFFILIATE_PERCENT;
      } else {
        userData.affiliatePercent = Number(userData.affiliatePercent) || 0;
      }

      if (
        userData.affiliateDuration === null ||
        userData.affiliateDuration === undefined ||
        userData.affiliateDuration === ""
      ) {
        userData.affiliateDuration = DEFAULT_AFFILIATE_DURATION;
      } else {
        userData.affiliateDuration = Number(userData.affiliateDuration) || 0;
      }

      if (
        userData.affiliatePercent < 0 ||
        userData.affiliatePercent > 100
      ) {
        alert("Phần trăm hoa hồng phải nằm trong khoảng 0% đến 100%.");
        return;
      }

      if (userData.affiliateDuration <= 0) {
        alert("Thời hạn affiliate phải lớn hơn 0 tháng.");
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/users/${id}`, {
        method: "PATCH",
        headers: getAdminHeaders(),
        body: JSON.stringify({ userData }),
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Không thể cập nhật người dùng");
      }
      setEditMode(false);

      // Cập nhật state
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === id ? { ...user, ...(result.data || userData) } : user
        )
      );
      setCurrentUser((prevUser) => ({
        ...prevUser,
        ...userData,
      }));

      alert("Đã cập nhật thông tin người dùng thành công!");
    } catch (error) {
      console.error("Lỗi khi cập nhật thông tin người dùng:", error);
      alert("Đã xảy ra lỗi khi cập nhật thông tin người dùng!");
    }
  };

  // Xử lý xóa người dùng
  const handleDeleteUser = async () => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa người dùng này?")) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/admin/users/${currentUser.id}`,
        {
          method: "DELETE",
          headers: getAdminHeaders(),
        }
      );
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Không thể xóa người dùng");
      }
      setShowUserModal(false);

      // Cập nhật state
      setUsers((prevUsers) =>
        prevUsers.filter((user) => user.id !== currentUser.id)
      );
    } catch (error) {
      console.error("Lỗi khi xóa người dùng:", error);
      alert("Đã xảy ra lỗi khi xóa người dùng!");
    }
  };

  // Xử lý thay đổi giá trị input
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    // Xử lý đặc biệt cho membershipExpiry
    if (name === "membershipExpiry" && value) {
      // Chuyển đổi từ datetime-local string sang timestamp
      processedValue = new Date(value).getTime();
    }

    // Xử lý đặc biệt cho membership
    if (name === "membership") {
      // Convert to number if not empty, otherwise keep as string
      processedValue = value === "" ? null : parseInt(value);
    }

    // Xử lý đặc biệt cho currentPoints
    if (name === "currentPoints") {
      const updatedUser = {
        ...currentUser,
        points: {
          ...currentUser.points,
          points: parseInt(value) || 0,
        },
      };
      setCurrentUser(updatedUser);
      return;
    }

    if (name === "affiliatePercent" || name === "affiliateDuration") {
      processedValue = value === "" ? "" : Number(value);
    }

    setCurrentUser({
      ...currentUser,
      [name]: processedValue,
    });
  };

  // Xử lý thay đổi trang
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Tạo phân trang
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-2 md:px-3 py-1 rounded ${
            currentPage === i
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex flex-wrap justify-center mt-4 gap-1">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-2 md:px-3 py-1 rounded ${
            currentPage === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          &laquo;
        </button>
        {pages}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-2 md:px-3 py-1 rounded ${
            currentPage === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          &raquo;
        </button>
      </div>
    );
  };

  // Hiển thị trạng thái membership
  const renderMembershipStatus = (user) => {
    if (!user.membership) {
      return (
        <span className="px-2 py-1 bg-gray-500 text-white text-xs rounded-full">
          Free
        </span>
      );
    }

    const isExpired =
      user.membershipExpiry && new Date(user.membershipExpiry) < new Date();

    if (isExpired) {
      return (
        <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">
          Hết hạn
        </span>
      );
    } else {
      // Tìm plan name từ membershipPlans
      const plan = membershipPlans.find(
        (p) => p.id === parseInt(user.membership)
      );
      const planName = plan ? plan.name : "Premium";

      return (
        <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">
          {planName}
        </span>
      );
    }
  };

  if (detailUserId) {
    return (
      <div className="p-4 md:p-6">
        <AdminUserDetail
          userId={detailUserId}
          onBack={() => setDetailUserId(null)}
          onViewPosts={() => {
            setDetailUserId(null);
            setSelectedUserId(detailUserId);
            setViewAutoPostSettings(false);
          }}
          onViewAutoSettings={() => {
            setDetailUserId(null);
            setSelectedUserId(detailUserId);
            setViewAutoPostSettings(true);
          }}
        />
      </div>
    );
  }

  if (selectedUserId) {
    if (viewAutoPostSettings) {
      return (
        <>
          {setActiveMenu &&
            typeof setActiveMenu === "function" &&
            setActiveMenu("posts")}
          <AdminAutoPostSettings
            userId={selectedUserId}
            onBack={() => {
              setSelectedUserId(null);
              setViewAutoPostSettings(false);
            }}
          />
        </>
      );
    } else {
      return (
        <>
          {setActiveMenu &&
            typeof setActiveMenu === "function" &&
            setActiveMenu("posts")}
          <AdminPostManagement userId={selectedUserId} />
        </>
      );
    }
  }

  return (
    <div className="p-6 admin-user-management">
      <div className="mb-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-4 mb-6">
          {/* Tổng số user */}
          <div className="bg-white rounded-lg shadow p-2 md:p-4 hover:shadow-lg transition-all duration-300">
            <h3 className="text-xs md:text-sm font-medium text-gray-500">
              Tổng số user
            </h3>
            <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1 md:mt-2">
              {pageInfo.total || 0}
            </p>
            <p className="text-[10px] md:text-xs text-gray-500 mt-1 line-clamp-2">
              {fromDate || toDate
                ? "Trong khoảng thời gian đã chọn"
                : "Tất cả user trong hệ thống"}
            </p>
          </div>

          {/* User Premium/Free */}
          <div className="bg-white rounded-lg shadow p-2 md:p-4 hover:shadow-lg transition-all duration-300">
            <h3 className="text-xs md:text-sm font-medium text-gray-500">
              Premium/Free
            </h3>
            {(() => {
              const premiumUsers = summary?.premiumUsers || 0;
              const freeUsers = summary?.freeUsers || 0;
              const premiumPercentage = pageInfo.total
                ? ((premiumUsers / pageInfo.total) * 100).toFixed(
                    1
                  )
                : 0;
              return (
                <>
                  <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1 md:mt-2">{`${premiumUsers}/${freeUsers}`}</p>
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1 line-clamp-2">
                    {`${premiumPercentage}% người dùng Premium`}
                  </p>
                </>
              );
            })()}
          </div>

          {/* Tổng số bài viết */}
          <div className="bg-white rounded-lg shadow p-2 md:p-4 hover:shadow-lg transition-all duration-300">
            <h3 className="text-xs md:text-sm font-medium text-gray-500">
              Tổng bài viết
            </h3>
            {(() => {
              const totalPosts = summary?.totalPosts || 0;
              const avgPosts = summary?.averagePosts || 0;
              const activeUsers = summary?.activePostUsers || 0;
              const activePercentage = pageInfo.total
                ? ((activeUsers / pageInfo.total) * 100).toFixed(1)
                : 0;
              return (
                <>
                  <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1 md:mt-2">
                    {totalPosts}
                  </p>
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1 line-clamp-3">
                    {`Trung bình ${avgPosts} bài/user`}
                    <br />
                    {`${activePercentage}% user có bài viết`}
                  </p>
                </>
              );
            })()}
          </div>

          {/* Tổng điểm */}
          <div className="bg-white rounded-lg shadow p-2 md:p-4 hover:shadow-lg transition-all duration-300">
            <h3 className="text-xs md:text-sm font-medium text-gray-500">
              Tổng điểm
            </h3>
            {(() => {
              const totalPoints = summary?.totalPoints || 0;
              const avgPoints = summary?.averagePoints || 0;
              const usersWithPoints = filteredUsers.filter(
                (user) => user.totalPoints > 0
              ).length;
              const pointsPercentage = pageInfo.total
                ? ((usersWithPoints / pageInfo.total) * 100).toFixed(1)
                : 0;
              return (
                <>
                  <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1 md:mt-2">
                    {totalPoints}
                  </p>
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1 line-clamp-3">
                    {`Trung bình ${avgPoints} điểm/user`}
                    <br />
                    {`${pointsPercentage}% user có điểm`}
                  </p>
                </>
              );
            })()}
          </div>

          {/* User mới */}
          <div className="bg-white rounded-lg shadow p-2 md:p-4 hover:shadow-lg transition-all duration-300">
            <h3 className="text-xs md:text-sm font-medium text-gray-500">
              {fromDate || toDate
                ? "User trong khoảng thời gian"
                : "User mới (7 ngày)"}
            </h3>
            {(() => {
              const newUsers =
                fromDate || toDate
                  ? pageInfo.total || 0
                  : summary?.newUsers7Days || 0;
              const percentage = pageInfo.total
                ? ((newUsers / pageInfo.total) * 100).toFixed(1)
                : 0;
              const avgPerDay =
                fromDate && toDate
                  ? (
                      newUsers /
                      ((new Date(toDate) - new Date(fromDate)) /
                        (1000 * 60 * 60 * 24))
                    ).toFixed(1)
                  : (newUsers / 7).toFixed(1);
              return (
                <>
                  <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1 md:mt-2">
                    {newUsers}
                  </p>
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1 line-clamp-2">
                    {fromDate || toDate
                      ? `${percentage}% của tổng số user`
                      : `Trung bình ${avgPerDay} user/ngày`}
                  </p>
                </>
              );
            })()}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Quản lý người dùng
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search input */}
              <div className="col-span-1 md:col-span-1">
                <label
                  htmlFor="search"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Tìm kiếm
                </label>
                <div className="relative">
                  <input
                    id="search"
                    type="text"
                    placeholder="Tìm kiếm theo ID, tên, email..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchText && (
                    <button
                      onClick={() => setSearchText("")}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Membership filter */}
              <div className="col-span-1 md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lọc theo membership
                </label>
                <div className="space-y-2">
                  {/* Quick select buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const allMemberships = [
                          null,
                          ...membershipPlans.map((p) => p.id),
                        ];
                        setSelectedMemberships(allMemberships);
                      }}
                      className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                    >
                      Chọn tất cả
                    </button>
                    <button
                      onClick={() => setSelectedMemberships([])}
                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
                    >
                      Bỏ chọn tất cả
                    </button>
                  </div>

                  {/* Membership checkboxes */}
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                    {/* Free option */}
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedMemberships.includes(null)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMemberships([
                              ...selectedMemberships,
                              null,
                            ]);
                          } else {
                            setSelectedMemberships(
                              selectedMemberships.filter((m) => m !== null)
                            );
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">Free</span>
                    </label>

                    {/* Membership plans */}
                    {membershipPlans.map((plan) => (
                      <label key={plan.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedMemberships.includes(plan.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMemberships([
                                ...selectedMemberships,
                                plan.id,
                              ]);
                            } else {
                              setSelectedMemberships(
                                selectedMemberships.filter((m) => m !== plan.id)
                              );
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">{plan.displayName}</span>
                      </label>
                    ))}
                  </div>

                  {selectedMemberships.length > 0 && (
                    <div className="text-xs text-gray-500">
                      Đã chọn {selectedMemberships.length} loại membership
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Date range filter */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lọc theo ngày tạo
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-grow">
                  <label
                    htmlFor="fromDate"
                    className="block text-xs text-gray-500 mb-1"
                  >
                    Từ ngày
                  </label>
                  <input
                    id="fromDate"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {fromDate && (
                    <button
                      onClick={() => setFromDate("")}
                      className="absolute right-2 top-[60%] transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="relative flex-grow">
                  <label
                    htmlFor="toDate"
                    className="block text-xs text-gray-500 mb-1"
                  >
                    Đến ngày
                  </label>
                  <input
                    id="toDate"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {toDate && (
                    <button
                      onClick={() => setToDate("")}
                      className="absolute right-2 top-[60%] transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                {(fromDate || toDate) && (
                  <button
                    onClick={() => {
                      setFromDate("");
                      setToDate("");
                    }}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm mt-4"
                  >
                    Xóa bộ lọc ngày
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <p className="mt-4 text-gray-600">
                  Đang tải dữ liệu người dùng...
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto -mx-6 md:mx-0">
                  <table className="min-w-full bg-white table-auto md:table-fixed">
                    <thead>
                      <tr className="bg-gray-100 text-gray-600 uppercase text-xs md:text-sm leading-normal">
                        <th className="py-2 px-2 md:py-3 md:px-6 text-left w-1/6">
                          ID
                        </th>
                        <th className="py-2 px-2 md:py-3 md:px-6 text-left w-1/12">
                          Membership
                        </th>
                        <th
                          className="py-2 px-2 md:py-3 md:px-6 text-left cursor-pointer w-1/6"
                          onClick={() => handleSort("membershipExpiry")}
                        >
                          <div
                            className="flex items-center gap-1 cursor-pointer"
                            onClick={() => handleSort("membershipExpiry")}
                          >
                            <span className="hidden md:inline">
                              Ngày hết hạn
                            </span>
                            <span className="md:hidden">Hết hạn</span>
                            {getSortIndicator("membershipExpiry")}
                          </div>
                        </th>
                        <th
                          className="py-2 px-2 md:py-3 md:px-6 text-left cursor-pointer w-1/12"
                          onClick={() => handleSort("totalPostCount")}
                        >
                          <div
                            className="flex items-center gap-1 cursor-pointer"
                            onClick={() => handleSort("totalPostCount")}
                          >
                            <span className="hidden md:inline">
                              Tổng bài viết
                            </span>
                            <span className="md:hidden">Bài viết</span>
                            {getSortIndicator("totalPostCount")}
                          </div>
                        </th>
                        <th className="py-2 px-2 md:py-3 md:px-6 text-left w-1/6 hidden md:table-cell">
                          Chi tiết bài viết
                        </th>
                        <th
                          className="py-2 px-2 md:py-3 md:px-6 text-left cursor-pointer w-1/12"
                          onClick={() => handleSort("totalPoints")}
                        >
                          <div
                            className="flex items-center gap-1 cursor-pointer"
                            onClick={() => handleSort("totalPoints")}
                          >
                            Điểm {getSortIndicator("totalPoints")}
                          </div>
                        </th>
                        <th className="py-2 px-2 md:py-3 md:px-6 text-left w-1/12">
                          <span className="hidden md:inline">Fanpage</span>
                          <span className="md:hidden">FP</span>
                        </th>
                        <th
                          className="py-2 px-2 md:py-3 md:px-6 text-left cursor-pointer w-1/6"
                          onClick={() => handleSort("createdAt")}
                        >
                          <div
                            className="flex items-center gap-1 cursor-pointer"
                            onClick={() => handleSort("createdAt")}
                          >
                            <span className="hidden md:inline">Ngày tạo</span>
                            <span className="md:hidden">Ngày</span>
                            {getSortIndicator("createdAt")}
                          </div>
                        </th>
                        <th className="py-2 px-2 md:py-3 md:px-6 text-center w-1/12">
                          Hành động
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600 text-sm">
                      {currentUsers.length > 0 ? (
                        currentUsers.map((user) => (
                          <tr
                            key={user.id}
                            className="border-b border-gray-200 hover:bg-gray-50"
                          >
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left whitespace-nowrap max-w-[80px] md:max-w-[100px] truncate text-xs md:text-sm">
                              {user.id}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left text-xs md:text-sm">
                              {renderMembershipStatus(user)}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left text-xs md:text-sm">
                              {user.membershipExpiry
                                ? new Date(
                                    user.membershipExpiry
                                  ).toLocaleDateString("vi-VN")
                                : "Chưa có"}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left whitespace-nowrap text-xs md:text-sm">
                              {user.totalPostCount || 0}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left whitespace-nowrap hidden md:table-cell text-xs md:text-sm">
                              {user.postCounts || "0/0/0/0"}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left whitespace-nowrap text-xs md:text-sm">
                              {user.totalPoints || 0}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left whitespace-nowrap text-xs md:text-sm">
                              {user.connectedFanpagesCount !== undefined
                                ? user.connectedFanpagesCount
                                : user.connectedFanpages
                                ? Array.isArray(user.connectedFanpages)
                                  ? user.connectedFanpages.length
                                  : Object.keys(user.connectedFanpages).length
                                : 0}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left whitespace-nowrap text-xs md:text-sm">
                              {user.createdAt
                                ? new Date(user.createdAt).toLocaleDateString(
                                    "vi-VN"
                                  )
                                : "Chưa có"}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-center">
                              <button
                                onClick={() => handleUserClick(user)}
                                className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-2 md:px-3 rounded-md text-xs transition duration-300"
                              >
                                Chi tiết
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="9" // Updated colspan for mobile view with new fanpage column
                            className="py-6 text-center text-gray-500"
                          >
                            Không tìm thấy người dùng nào
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {renderPagination()}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserManagement;
